//----------------------------------
// Imports
//----------------------------------
import i18n from '@/i18n';
import config from './config';

//----------------------------------
// Constants
//----------------------------------
const { APP_BASE_URL, API_DEBUG } = config;

const BONES_HEADER = 'X-Bones-Details';

const COLOR_GREEN = '#008800';
const COLOR_ORANGE = '#884f0e';
const COLOR_RED = '#880000';

const HTTP_STASUSES = {
  200: { color: COLOR_GREEN, title: 'Success' },
  304: { color: COLOR_ORANGE, title: 'Warning' },
  400: { color: COLOR_RED, title: 'Error' },
  403: { color: COLOR_RED, title: 'Error' },
  404: { color: COLOR_RED, title: 'Error' },
};

//----------------------------------
// Internal Methods
//----------------------------------
const internal = {
  fetch: async (method, uri, parameters = null, body = null) => {
    const headers = new Headers({ Accept: 'application/json', 'Accept-language': i18n.locale });
    const appBaseUrl = APP_BASE_URL === '/' ? '' : APP_BASE_URL;
    const url = new URL(`${appBaseUrl}/api/${uri}`, window.location.origin);
    if (parameters !== null) {
      const keys = Object.keys(parameters);
      keys.forEach((key) => {
        url.searchParams.append(key, parameters[key]);
      });
    }
    // eslint-disable-next-line no-return-await
    return await window.fetch(url, { method, headers, credentials: 'same-origin', body: body ? internal.convertToFormData(body) : null });
  },
  convertToFormData: (obj, form, namespace) => {
    const formData = form || new FormData();
    const objectKeys = Object.keys(obj);
    objectKeys.forEach((key) => {
      const formKey = namespace ? `${namespace}[${key}]` : key;
      if (obj[key] !== null && typeof obj[key] === 'object' && !(obj[key] instanceof File) && !(obj[key] instanceof Blob)) {
        internal.convertToFormData(obj[key], formData, formKey); // if object, but not a File, use recursivity
      } else {
        formData.append(formKey, obj[key]);
      }
    });
    return formData;
  },
  log: (status, details, url, data) => {
    if (API_DEBUG) {
      const { title, color } = HTTP_STASUSES[status];
      // eslint-disable-next-line no-console
      console.log(
        `%c BONES API ${title} %c ${details} - %c${url}`,
        `background-color:${color};color:#fff;border-radius:0.5em;`,
        'background:inherit;color:inherit;',
        'background:#333;color:#aaa;padding:0 5px;font-style:italic;border-radius:0.1em;',
        data,
      );
    }
  },
};

export default {
  get: async (uri, parameters = null) => {
    const response = await internal.fetch('GET', uri, parameters);
    if (response.ok) {
      // eslint-disable-next-line no-return-await
      return await response.json();
    }
    const bonesResponseDetails = response.headers.get(BONES_HEADER);
    if (bonesResponseDetails) {
      internal.log(response.status, bonesResponseDetails, response.url);
    }
    return null;
  },

  post: async (uri, parameters = null, data = null) => {
    const response = await internal.fetch('POST', uri, parameters, data);
    if (response.ok) {
      const bonesResponseDetails = response.headers.get(BONES_HEADER);
      if (bonesResponseDetails) {
        internal.log(response.status, bonesResponseDetails, response.url, data);
      }
      try {
        // eslint-disable-next-line no-return-await
        return await response.json();
      } catch (err) {
        return response;
      }
    }
    return false;
  },

  upload: (files, model, id, progressHandler = null) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const appBaseUrl = APP_BASE_URL === '/' ? '' : APP_BASE_URL;
    xhr.open('POST', `${window.location.origin}${appBaseUrl}/files/`);
    xhr.onload = () => {
      const status = xhr.status;
      if (status >= 200 && status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`${status}: ${xhr.statusText}`));
      }
    };
    xhr.upload.addEventListener('progress', (evt) => {
      if (evt.lengthComputable) {
        progressHandler(evt);
      }
    });
    const filesArr = files.constructor === Array ? files : [files];
    xhr.send(internal.convertToFormData({ files: filesArr, model, id }));
  }),
};
